import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Plus, Edit, Trash2, Star, Building, Save, Settings } from 'lucide-react';
import { useApi, useApiMutation } from '../hooks/useApi';
import { companiesApi } from '../services/api';
import toast from 'react-hot-toast';
import CompanySettings from './CompanySettings';

const CompanyManager = ({ currentCompany, onCompanySelect }) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [settingsCompany, setSettingsCompany] = useState(null);
  const [newCompany, setNewCompany] = useState({
    name: '',
    address: '',
    city: '',
    phone: '',
    email: '',
    website: '',
    primaryColor: '#2563eb',
    secondaryColor: '#1e40af',
    // Extended fields
    taxId: '',
    iban: '',
    bic: '',
    bankName: '',
    companyRegister: '',
    ceo: '',
    termsAndConditions: '',
    logo: null
  });

  const { data: companies, loading, refetch } = useApi(() => companiesApi.getAll());
  const { mutate: createCompany, loading: creating } = useApiMutation();
  const { mutate: updateCompany, loading: updating } = useApiMutation();
  const { mutate: deleteCompany, loading: deleting } = useApiMutation();
  const { mutate: setDefaultCompany } = useApiMutation();

  const handleCreateCompany = async () => {
    try {
      const created = await createCompany(() => companiesApi.create(newCompany));
      
      toast.success('✅ Firma erfolgreich erstellt!');
      
      setIsCreateModalOpen(false);
      setNewCompany({
        name: '',
        address: '',
        city: '',
        phone: '',
        email: '',
        website: '',
        primaryColor: '#2563eb',
        secondaryColor: '#1e40af',
        // Extended fields
        taxId: '',
        iban: '',
        bic: '',
        bankName: '',
        companyRegister: '',
        ceo: '',
        termsAndConditions: '',
        logo: null
      });
      refetch();
    } catch (error) {
      toast.error('❌ Firma konnte nicht erstellt werden');
    }
  };

  const handleUpdateCompany = async () => {
    try {
      await updateCompany(() => companiesApi.update(editingCompany.id, editingCompany));
      
      toast.success('✅ Firma erfolgreich aktualisiert!');
      
      setIsEditModalOpen(false);
      setEditingCompany(null);
      refetch();
    } catch (error) {
      toast.error('❌ Firma konnte nicht aktualisiert werden');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (window.confirm('Sind Sie sicher, dass Sie diese Firma löschen möchten?')) {
      try {
        await deleteCompany(() => companiesApi.delete(companyId));
        
        toast.success('✅ Firma erfolgreich gelöscht!');
        
        refetch();
      } catch (error) {
        toast.error('❌ Firma konnte nicht gelöscht werden');
      }
    }
  };

  const handleSetDefault = async (companyId) => {
    try {
      await setDefaultCompany(() => companiesApi.setDefault(companyId));
      
      toast.success('✅ Standard-Firma gesetzt!');
      
      refetch();
    } catch (error) {
      toast.error('❌ Standard-Firma konnte nicht gesetzt werden');
    }
  };

  const openEditModal = (company) => {
    setEditingCompany({ ...company });
    setIsEditModalOpen(true);
  };

  const openSettingsModal = (company) => {
    setSettingsCompany({ ...company });
    setIsSettingsModalOpen(true);
  };

  const handleSaveSettings = async (updatedCompanyInfo) => {
    try {
      // Use the passed companyInfo if provided, otherwise use settingsCompany
      const companyToSave = updatedCompanyInfo || settingsCompany;
      console.log('🔍 Saving company settings:', companyToSave)
      const updated = await updateCompany(() => companiesApi.update(companyToSave.id, companyToSave));
      
      toast.success('✅ Erfolgreich gespeichert!', {
        duration: 3000,
      });
      
      // Update the settingsCompany state with saved data to avoid flickering
      setSettingsCompany(updated || companyToSave);
      
      // Update current company if it's the one being edited
      if (onCompanySelect && companyToSave.id === currentCompany?.id) {
        onCompanySelect(updated || companyToSave)
      }
      
      // DO NOT refetch - this causes the modal to flicker
      // DO NOT close modal - user can continue editing or close manually
      
    } catch (error) {
      console.error('🔍 Error saving settings:', error)
      toast.error('❌ Fehler beim Speichern');
    }
  };

  const selectCompany = (company) => {
    onCompanySelect(company);
    toast.success(`✅ ${company.name} als aktive Firma ausgewählt`);
  };

  if (loading) {
    return <div className="flex justify-center py-8">Lade Firmen...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Building className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-slate-800">Firmen-Verwaltung</h2>
        </div>
        
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Neue Firma
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Neue Firma erstellen</DialogTitle>
            </DialogHeader>
            <CompanySettings
              companyInfo={newCompany}
              setCompanyInfo={setNewCompany}
              onSave={handleCreateCompany}
              saveButtonText="Firma erstellen"
              loading={creating}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Firma</TableHead>
              <TableHead>Kontakt</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {companies?.map((company) => (
              <TableRow 
                key={company.id}
                className={currentCompany?.id === company.id ? 'bg-blue-50' : ''}
              >
                <TableCell>
                  <div className="flex items-center space-x-3">
                    <div>
                      <p className="font-medium">{company.name}</p>
                      <p className="text-sm text-gray-500">{company.address}, {company.city}</p>
                      {company.logo && (
                        <div className="mt-1 flex items-center space-x-2">
                          <div className="w-6 h-6 rounded overflow-hidden border border-slate-200">
                            <img 
                              src={company.logo} 
                              alt="Logo" 
                              className="w-full h-full object-contain bg-white"
                              onLoad={(e) => {
                                e.target.nextElementSibling.style.display = 'none'
                                const label = e.target.closest('.flex').querySelector('.logo-label')
                                if (label) label.style.display = 'inline'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none'
                                e.target.nextElementSibling.style.display = 'flex'
                              }}
                            />
                            <div 
                              className="w-full h-full bg-slate-100 flex items-center justify-center text-xs text-slate-500" 
                              style={{display: 'none'}}
                            >
                              ❌
                            </div>
                          </div>
                          <span className="logo-label text-xs text-green-600" style={{display: 'none'}}>✅ Logo</span>
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <p>{company.phone}</p>
                    <p className="text-gray-500">{company.email}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {company.isDefault && (
                      <Badge className="bg-yellow-100 text-yellow-800">
                        <Star className="w-3 h-3 mr-1" />
                        Standard
                      </Badge>
                    )}
                    {currentCompany?.id === company.id && (
                      <Badge className="bg-blue-100 text-blue-800">
                        Aktiv
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      size="sm" 
                      variant={currentCompany?.id === company.id ? "default" : "outline"}
                      onClick={() => selectCompany(company)}
                      disabled={currentCompany?.id === company.id}
                      data-testid="company-select-btn"
                    >
                      {currentCompany?.id === company.id ? '✓ Aktiv' : 'Verwenden'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => openSettingsModal(company)}
                      title="Einstellungen bearbeiten"
                    >
                      <Settings className="w-3 h-3" />
                    </Button>
                    {!company.isDefault && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleSetDefault(company.id)}
                        title="Als Standard setzen"
                      >
                        <Star className="w-3 h-3" />
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleDeleteCompany(company.id)}
                      className="text-red-600"
                      disabled={company.isDefault}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Firma bearbeiten</DialogTitle>
          </DialogHeader>
          {editingCompany && (
            <CompanyForm
              company={editingCompany}
              onChange={setEditingCompany}
              onSave={handleUpdateCompany}
              loading={updating}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={isSettingsModalOpen} onOpenChange={(open) => {
        console.log('🔍 Settings modal onOpenChange:', open)
        
        // Only close when explicitly requested
        if (!open) {
          setIsSettingsModalOpen(false)
          setTimeout(() => {
            setSettingsCompany(null)
          }, 200)
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Firmeneinstellungen - {settingsCompany?.name}
            </DialogTitle>
          </DialogHeader>
          {settingsCompany && (
            <CompanySettings
              companyInfo={settingsCompany}
              setCompanyInfo={(updatedCompany) => {
                console.log('🔍 Updating company info:', updatedCompany)
                setSettingsCompany(updatedCompany)
              }}
              onSave={handleSaveSettings}
              saveButtonText="Einstellungen speichern"
              loading={updating}
              onCompanyUpdate={(updatedCompany) => {
                // This callback is used to get the latest company data before saving
                setSettingsCompany(updatedCompany)
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CompanyForm = ({ company, onChange, onSave, loading }) => {
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(company.logo || null);

  // Update logo preview when company.logo changes
  useEffect(() => {
    if (company.logo && company.logo !== logoPreview) {
      setLogoPreview(company.logo);
    }
  }, [company.logo]);

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/') || file.size > 2 * 1024 * 1024) {
      toast.error('❌ Bitte wählen Sie eine Bilddatei unter 2MB');
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Logo = e.target.result;
      setLogoPreview(base64Logo);
      // Wichtig: Logo an parent weitergeben
      onChange({ ...company, logo: base64Logo });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6 max-h-[80vh] overflow-y-auto">
      
      {/* Basis-Informationen */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-800">Basis-Informationen</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Firmenname *</Label>
            <Input
              value={company.name}
              onChange={(e) => onChange({ ...company, name: e.target.value })}
              placeholder="Ihre Agentur GmbH"
              required
            />
          </div>
          
          <div>
            <Label>Website</Label>
            <Input
              value={company.website}
              onChange={(e) => onChange({ ...company, website: e.target.value })}
              placeholder="www.ihre-agentur.de"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Adresse</Label>
            <Input
              value={company.address}
              onChange={(e) => onChange({ ...company, address: e.target.value })}
              placeholder="Musterstraße 123"
            />
          </div>
          
          <div>
            <Label>PLZ & Ort</Label>
            <Input
              value={company.city}
              onChange={(e) => onChange({ ...company, city: e.target.value })}
              placeholder="12345 Musterstadt"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Telefon</Label>
            <Input
              value={company.phone}
              onChange={(e) => onChange({ ...company, phone: e.target.value })}
              placeholder="+49 123 456789"
            />
          </div>
          
          <div>
            <Label>E-Mail</Label>
            <Input
              type="email"
              value={company.email}
              onChange={(e) => onChange({ ...company, email: e.target.value })}
              placeholder="info@ihre-agentur.de"
            />
          </div>
        </div>
      </div>

      {/* Logo Upload */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-800">Branding</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Firmenlogo</Label>
            <div className="mt-2">
              <input
                type="file"
                onChange={handleLogoUpload}
                accept="image/*"
                className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {logoPreview && (
                <div className="mt-3">
                  <img src={logoPreview} alt="Logo Vorschau" className="h-16 w-auto border rounded" />
                </div>
              )}
            </div>
          </div>

          <div>
            <Label>Primärfarbe</Label>
            <div className="mt-2 space-y-2">
              <Input
                type="color"
                value={company.primaryColor || '#2563eb'}
                onChange={(e) => onChange({ ...company, primaryColor: e.target.value })}
                className="h-12 w-full"
              />
              <Input
                value={company.primaryColor || '#2563eb'}
                onChange={(e) => onChange({ ...company, primaryColor: e.target.value })}
                placeholder="#2563eb"
                className="text-center"
              />
            </div>
          </div>
          
          <div>
            <Label>Landing Page Hintergrundfarbe</Label>
            <div className="mt-2 space-y-2">
              <Input
                type="color"
                value={company.landingPageBgColor || '#f1f5f9'}
                onChange={(e) => onChange({ ...company, landingPageBgColor: e.target.value })}
                className="h-12 w-full"
              />
              <Input
                value={company.landingPageBgColor || '#f1f5f9'}
                onChange={(e) => onChange({ ...company, landingPageBgColor: e.target.value })}
                placeholder="#f1f5f9"
                className="text-center"
              />
              <p className="text-xs text-slate-500">Nur für Landing Page, nicht im PDF (bleibt weiß)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Finanzielle Informationen */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-800">Finanzielle Informationen</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Steuernummer</Label>
            <Input
              value={company.taxId || ''}
              onChange={(e) => onChange({ ...company, taxId: e.target.value })}
              placeholder="123/456/78901"
            />
          </div>
          
          <div>
            <Label>IBAN</Label>
            <Input
              value={company.iban || ''}
              onChange={(e) => onChange({ ...company, iban: e.target.value })}
              placeholder="DE89 3704 0044 0532 0130 00"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>BIC</Label>
            <Input
              value={company.bic || ''}
              onChange={(e) => onChange({ ...company, bic: e.target.value })}
              placeholder="COBADEFFXXX"
            />
          </div>
          
          <div>
            <Label>Bank</Label>
            <Input
              value={company.bankName || ''}
              onChange={(e) => onChange({ ...company, bankName: e.target.value })}
              placeholder="Commerzbank AG"
            />
          </div>
        </div>
      </div>

      {/* Rechtliche Informationen */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-800">Rechtliche Informationen</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Handelsregisternummer</Label>
            <Input
              value={company.companyRegister || ''}
              onChange={(e) => onChange({ ...company, companyRegister: e.target.value })}
              placeholder="HRB 123456"
            />
          </div>
          
          <div>
            <Label>Geschäftsführer</Label>
            <Input
              value={company.ceo || ''}
              onChange={(e) => onChange({ ...company, ceo: e.target.value })}
              placeholder="Max Mustermann"
            />
          </div>
        </div>
      </div>

      {/* AGBs */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-800">Allgemeine Geschäftsbedingungen</h3>
        
        <div>
          <Label>AGB-Text (wird automatisch an PDFs angehängt)</Label>
          <Textarea
            value={company.termsAndConditions || ''}
            onChange={(e) => onChange({ ...company, termsAndConditions: e.target.value })}
            placeholder={`**§ 1 Geltung**
Diese Geschäftsbedingungen gelten für alle Verträge...

**§ 2 Zahlungsbedingungen**
• 30 Tage netto nach Rechnungsstellung
• Bei Verzug werden Zinsen berechnet

Verwenden Sie **Text** für Überschriften und • für Aufzählungen.`}
            rows={6}
            className="font-mono text-sm"
          />
          <div className="text-xs text-slate-500 mt-1">
            💡 Verwenden Sie **Text** für Überschriften und • für Aufzählungen
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t">
        <Button 
          onClick={onSave}
          disabled={loading || !company.name}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {loading ? 'Erstelle...' : 'Firma erstellen'}
        </Button>
      </div>
    </div>
  )
};

export default CompanyManager;